import { DefaultButton, DetailsList, IColumn, Icon, Link, MessageBar, SelectionMode, Spinner } from '@fluentui/react';
import { makeAutoObservable } from 'mobx';
import { observer } from 'mobx-react';
import { useEffect } from 'react';
import { EntityAPI, EntityType, IDocumentSearchScope, IUserConfigPackage } from '../../../API';
import SearchScopeEditor, { SearchScopeEditorState } from './SearchScopeEditor';

export class SearchScopesListState {
	
	public userConfig: IUserConfigPackage;

	public loading: boolean = false;
	public loaded: boolean = false;

	public editState: SearchScopeEditorState;
	
	public ids: string[] = [];
	
	update?: (ids: string[]) => void;
	
	items: IDocumentSearchScope[] = [];

	constructor(userConfig: IUserConfigPackage, update?:(ids:string[]) => void) {
		this.userConfig = userConfig;
		this.update = update;
		
		this.editState = new SearchScopeEditorState(this.userConfig, this.delete.bind(this), this.save.bind(this));

		makeAutoObservable(this);
	}
	
	public setSelected(ids:string[]) {
		this.ids = ids || [];
	}
	
	public async load(): Promise<void> {
		this.loading = true;

		let api = await this.getApi();
		let items = await api.all();
		this.setItems(items.items);
	}

	private async getApi() : Promise<EntityAPI<IDocumentSearchScope>> {
		return await EntityAPI.fromEntityType(
			EntityType.DocumentSearchScope,
			this.userConfig,
		) as EntityAPI<IDocumentSearchScope>;
	}

	ensureInitialLoaded() {
		if (!this.loading && !this.loaded) {
			this.load();
		}
	}

	setItems(items: IDocumentSearchScope[]) {
		this.loading = false;
		this.loaded = true;
		this.items = items;
	}
	
	edit(item: IDocumentSearchScope) {
		if(this.editState) {
			this.editState.setItem(item);
			this.editState.dialog.setVisible(true);
		}
	}
	
	public showAddNewDialog() {

		if(this.editState) {
			var newItem = {
				Scope: '',
				Title: 'New search scope'
			} as any as IDocumentSearchScope;

			this.editState.setItem(newItem);
			this.editState.dialog.setVisible(true);
		}
	}

	public async save(original:IDocumentSearchScope, updated:IDocumentSearchScope) : Promise<void> {
		
		//Update the original with the new properties
		Object.assign(original, updated);

		if(!original.Id) {
			//There was no id - it needs added to the list
			this.items.push(original);
		}

		let api = await this.getApi();
		original.Id = await api.upsert(updated, []);

		this.updated();
	}

	public async delete(item:IDocumentSearchScope) {
		this.remove(item);
		let api = await this.getApi();
		await api.delete([item.Id]);
	}

	public remove(item:IDocumentSearchScope) {
		this.items = this.items.filter(p => p !== item);
		this.updated();
	}
	
	public updated() {
		//Force a rerender
		this.items = this.items.slice();

		//Notify of new values
		if(this.update) {
			this.update(this.items.map(p => p.Id));
		}
	}
}

function SearchScopesList({ state }: { state: SearchScopesListState }) {
	useEffect(() => {
		state.ensureInitialLoaded();
	}, [state]);

	let columns: IColumn[] = [
		{
			key: 'name',
			name: 'Name',
			minWidth: 250,
			maxWidth: 250,
			onRender: (item: IDocumentSearchScope) => <Link onClick={() => {
				state.edit(item);
			}}>{item.Title}</Link>,
		},
		{
			key: 'scope',
			name: 'Scope',
			minWidth: 40,
			onRender: (item: IDocumentSearchScope) => <span>
				<span>{item.Scope}</span>
			</span>,
		},
		{
			key: 'delete',
			name: '',
			minWidth: 100,
			onRender: (item: IDocumentSearchScope) => <Icon style={{fontSize:20, cursor:'pointer'}} iconName={'Delete'} onClick={() => { state.delete(item) }} />,
		}
	];


	if (state.loading) {
		return <Spinner></Spinner>;
	}
	
	return (
		<>
			<>
				{state.editState && <SearchScopeEditor state={state.editState} /> }
				
				<MessageBar>
					Configure search scopes for the end-user document search view.
				</MessageBar>
					
				{(state?.items && state.items.length) ?
					<DetailsList selectionMode={SelectionMode.none} columns={columns} items={state?.items || []} /> : 
					<></>
				}
			</>

			<div style={{marginTop:20}}>
				<DefaultButton onClick={state.showAddNewDialog.bind(state)}>Add</DefaultButton>
			</div>
		</>
	);
}

export default observer(SearchScopesList);
